import { hexToHslChannels, publicThemeBrandTokenCss } from '@tebuto/lib/public-theme-css'
import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { isLightHexColor, resolveWidgetTextOnPrimary } from './color-utils'
import { defaultTheme } from './defaultTheme'
import { WidgetThemeConfig } from './types'

interface ThemeContextValue {
    theme: WidgetThemeConfig
    generateCSS: (theme: WidgetThemeConfig) => string
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function useTheme() {
    const context = useContext(ThemeContext)
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider')
    }
    return context
}

// Color manipulation utilities
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result
        ? {
              r: Number.parseInt(result[1], 16),
              g: Number.parseInt(result[2], 16),
              b: Number.parseInt(result[3], 16)
          }
        : null
}

function rgbToHex(r: number, g: number, b: number): string {
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`
}

function lighten(color: string, amount: number): string {
    const rgb = hexToRgb(color)
    if (!rgb) return color

    const { r, g, b } = rgb
    const newR = Math.min(255, Math.floor(r + (255 - r) * amount))
    const newG = Math.min(255, Math.floor(g + (255 - g) * amount))
    const newB = Math.min(255, Math.floor(b + (255 - b) * amount))

    return rgbToHex(newR, newG, newB)
}

function darken(color: string, amount: number): string {
    const rgb = hexToRgb(color)
    if (!rgb) return color

    const { r, g, b } = rgb
    const newR = Math.floor(r * (1 - amount))
    const newG = Math.floor(g * (1 - amount))
    const newB = Math.floor(b * (1 - amount))

    return rgbToHex(newR, newG, newB)
}

function generateColorShades(baseColor: string) {
    return {
        25: lighten(baseColor, 0.975),
        50: lighten(baseColor, 0.95),
        100: lighten(baseColor, 0.9),
        200: lighten(baseColor, 0.75),
        300: lighten(baseColor, 0.6),
        400: lighten(baseColor, 0.3),
        500: baseColor,
        600: darken(baseColor, 0.1),
        700: darken(baseColor, 0.2),
        800: darken(baseColor, 0.3),
        900: darken(baseColor, 0.4)
    }
}

function generateCSS(theme: WidgetThemeConfig): string {
    const primaryColor = theme.primaryColor || defaultTheme.primaryColor
    const backgroundColor = theme.backgroundColor || '#ffffff'
    const textColor = theme.textPrimary || '#1e293b'

    const primaryShades = generateColorShades(primaryColor)

    const isLightBg = isLightHexColor(backgroundColor)
    const secondaryTextColor = theme.textSecondary || (isLightBg ? darken(textColor, 0.3) : lighten(textColor, 0.3))
    const placeholderTextColor = isLightBg ? lighten(secondaryTextColor, 0.55) : darken(secondaryTextColor, 0.2)
    const textOnPrimary = resolveWidgetTextOnPrimary(primaryShades[600], backgroundColor, theme.textOnPrimary)

    const borderColor = theme.borderColor || (isLightBg ? darken(backgroundColor, 0.1) : lighten(backgroundColor, 0.1))
    const availableDayBackgroundColor =
        theme.calendarAvailableDay || (isLightBg ? primaryShades[100] : lighten(backgroundColor, 0.82))
    const unavailableDayBackgroundColor = isLightBg ? '#f9fafb' : lighten(backgroundColor, 0.22)

    const foregroundChannels = hexToHslChannels(textColor) ?? '220 13% 13%'
    const foregroundSecondaryChannels = hexToHslChannels(secondaryTextColor) ?? '217 10% 25%'
    const backgroundChannels = hexToHslChannels(backgroundColor) ?? '0 0% 100%'
    const borderChannels = hexToHslChannels(borderColor) ?? '220 13% 91%'
    const borderSecondaryChannels =
        hexToHslChannels(isLightBg ? darken(borderColor, 0.08) : lighten(borderColor, 0.08)) ?? '216 12% 84%'
    const brandTokens = publicThemeBrandTokenCss(primaryColor)
    const brandChannels = hexToHslChannels(primaryShades[600]) ?? '175 100% 28%'
    const brandHoverChannels = hexToHslChannels(primaryShades[700]) ?? '180 100% 20%'

    return `
    .widget-container {
        /* Primary color system - Tebuto branding */
        --widget-primary: ${primaryColor};
        --widget-primary-25: ${primaryShades[25]};
        --widget-primary-50: ${primaryShades[50]};
        --widget-primary-100: ${primaryShades[100]};
        --widget-primary-200: ${primaryShades[200]};
        --widget-primary-300: ${primaryShades[300]};
        --widget-primary-400: ${primaryShades[400]};
        --widget-primary-500: ${primaryShades[500]};
        --widget-primary-600: ${primaryShades[600]};
        --widget-primary-700: ${primaryShades[700]};
        --widget-primary-800: ${primaryShades[800]};
        --widget-primary-900: ${primaryShades[900]};

        /* Text colors - defaults */
        --widget-text-primary: ${textColor};
        --widget-text-secondary: ${secondaryTextColor};
        --widget-text-placeholder: ${placeholderTextColor};
        --widget-text-on-primary: ${textOnPrimary};

        /* Background colors */
        --widget-bg-primary: ${backgroundColor};
        --widget-calendar-available-day: ${availableDayBackgroundColor};
        --widget-calendar-unavailable-day: ${unavailableDayBackgroundColor};

        /* Border colors */
        --widget-border: ${borderColor};

        /* Semantic tokens for page-parity seminar markup — scoped so app/wizard chrome
           keeps Tebuto defaults while the embed uses the therapist theme. */
        --background: ${backgroundChannels};
        --foreground: ${foregroundChannels};
        --foreground-secondary: ${foregroundSecondaryChannels};
        --border: ${borderChannels};
        --border-secondary: ${borderSecondaryChannels};
        --seminar-page-primary-color: ${primaryColor};
        --seminar-page-background-color: ${backgroundColor};
        --seminar-page-text-primary: ${textColor};
        --seminar-page-text-secondary: ${secondaryTextColor};
        --seminar-page-border-color: ${borderColor};
        ${brandTokens}

        /* Scoped to .widget-container to avoid overriding app-level --primary-brand / --primary-hover */
        --primary-brand: ${brandChannels};
        --primary-hover: ${brandHoverChannels};

        /* Generic radius for Tailwind rounded utilities */
        --radius: 0.5rem;
    }`
}

interface ThemeProviderProps {
    children: React.ReactNode
    theme: WidgetThemeConfig
}

export function ThemeProvider({ children, theme }: ThemeProviderProps) {
    const contextValue = useMemo(
        () => ({
            theme,
            generateCSS
        }),
        [theme]
    )

    const css = useMemo(() => generateCSS(theme), [theme])

    useEffect(() => {
        const existing =
            document.getElementById('tebuto-widget-theme') || document.getElementById('widget-theme-styles')
        if (existing) {
            ;(existing as HTMLStyleElement).textContent = css
        }
    }, [css])

    return (
        <ThemeContext.Provider value={contextValue}>
            <style id="tebuto-widget-theme" dangerouslySetInnerHTML={{ __html: css }} />
            {children}
        </ThemeContext.Provider>
    )
}
